/**
 * Service for syncing pending uploads when online
 */

import apiService from './api';
import offlineStorage, { PendingUpload } from './offlineStorage';

const MAX_RETRY_COUNT = 3;
const SYNC_INTERVAL = 30000; // 30 seconds

export interface SyncResult {
  successful: number;
  failed: number;
  total: number;
  errors: Array<{ uploadId: string; error: string }>;
}

class OfflineSyncService {
  private isSyncing = false;
  private syncInterval: NodeJS.Timeout | null = null;
  private listeners: Array<(result: SyncResult) => void> = [];

  /**
   * Start automatic syncing when online
   */
  startAutoSync(): void {
    // Clear any existing interval
    this.stopAutoSync();

    // Sync immediately if online
    if (navigator.onLine) {
      this.syncPendingUploads().catch((error) => {
        console.error('Error during initial automatic sync:', error);
      });
    }

    // Set up periodic sync
    this.syncInterval = setInterval(() => {
      if (navigator.onLine && !this.isSyncing) {
        this.syncPendingUploads().catch((error) => {
          console.error('Error during periodic automatic sync:', error);
        });
      }
    }, SYNC_INTERVAL);

    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  /**
   * Stop automatic syncing
   */
  stopAutoSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }

    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }

  /**
   * Handle online event
   */
  private handleOnline = (): void => {
    console.log('Device is online, starting sync...');
    this.syncPendingUploads().catch((error) => {
      console.error('Error during automatic sync on online event:', error);
    });
  };

  /**
   * Handle offline event
   */
  private handleOffline = (): void => {
    console.log('Device is offline, pausing sync');
  };

  /**
   * Manually trigger sync
   */
  async manualSync(): Promise<SyncResult> {
    return this.syncPendingUploads();
  }

  /**
   * Sync all pending uploads
   */
  async syncPendingUploads(): Promise<SyncResult> {
    if (this.isSyncing) {
      console.log('Sync already in progress');
      return {
        successful: 0,
        failed: 0,
        total: 0,
        errors: [],
      };
    }

    if (!navigator.onLine) {
      console.log('Device is offline, skipping sync');
      return {
        successful: 0,
        failed: 0,
        total: 0,
        errors: [],
      };
    }

    this.isSyncing = true;

    try {
      const pendingUploads = await offlineStorage.getAllPendingUploads();

      if (pendingUploads.length === 0) {
        return {
          successful: 0,
          failed: 0,
          total: 0,
          errors: [],
        };
      }

      console.log(`Syncing ${pendingUploads.length} pending uploads...`);

      const result: SyncResult = {
        successful: 0,
        failed: 0,
        total: pendingUploads.length,
        errors: [],
      };

      // Process uploads sequentially to avoid overwhelming the server
      for (const upload of pendingUploads) {
        try {
          await this.processUpload(upload);
          result.successful++;
        } catch (error: any) {
          result.failed++;
          const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
          result.errors.push({
            uploadId: upload.id,
            error: errorMessage,
          });

          // Update retry count or mark as failed
          if (upload.retryCount < MAX_RETRY_COUNT) {
            await offlineStorage.updatePendingUpload(upload.id, {
              retryCount: upload.retryCount + 1,
              error: errorMessage,
            });
          } else {
            // Max retries reached, keep in storage but mark error
            await offlineStorage.updatePendingUpload(upload.id, {
              error: `Max retries reached: ${errorMessage}`,
            });
          }
        }
      }

      console.log(`Sync complete: ${result.successful} successful, ${result.failed} failed`);

      // Notify listeners
      this.notifyListeners(result);

      return result;
    } catch (error) {
      console.error('Error getting pending uploads:', error);
      return {
        successful: 0,
        failed: 0,
        total: 0,
        errors: [],
      };
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Process a single upload
   */
  private async processUpload(upload: PendingUpload): Promise<void> {
    // Upload to server
    await apiService.uploadDiary(
      upload.content,
      upload.delimiter,
      upload.userId,
      upload.source,
      upload.autoCategorize
    );

    // Delete from IndexedDB on success
    await offlineStorage.deletePendingUpload(upload.id);
  }

  /**
   * Subscribe to sync events
   */
  onSync(callback: (result: SyncResult) => void): () => void {
    this.listeners.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners of sync result
   */
  private notifyListeners(result: SyncResult): void {
    this.listeners.forEach((callback) => {
      try {
        callback(result);
      } catch (error) {
        console.error('Error in sync listener:', error);
      }
    });
  }

  /**
   * Check if currently syncing
   */
  get syncing(): boolean {
    return this.isSyncing;
  }
}

// Export singleton instance
const offlineSync = new OfflineSyncService();
export default offlineSync;
